export type RequestStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'CANCELLED'

/** The field types the custom-field engine offers; a request form is built from these. */
export type CustomFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'DROPDOWN' | 'FILE' | 'LOOKUP'

export interface RequestTypeView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  category: string | null
  requiresAttachment: boolean
  /** The service level this type promises; the engine escalates on the step's own hours. */
  slaHours: number | null
  letterTemplateId: string | null
  icon: string | null
  active: boolean
  sortOrder: number
}

/**
 * One field of a request form.
 *
 * These are custom field definitions — the same rows the admin custom-fields screen edits.
 * There is no second field table, so there is one definition of a form and one validator.
 */
export interface RequestFormField {
  id: string
  code: string
  label: string
  helpText: string | null
  fieldType: CustomFieldType
  required: boolean
  options: { value?: string; label?: string }[]
  regex: string | null
  minNumber: number | null
  maxNumber: number | null
  minLength: number | null
  maxLength: number | null
  minDate: string | null
  maxDate: string | null
  sortOrder: number
  active: boolean
}

export interface GenericRequestView {
  id: string
  requestNo: string | null
  requestTypeId: string
  requestTypeCode: string | null
  requestTypeName: string | null
  employeeId: string
  subject: string | null
  payload: Record<string, unknown>
  status: RequestStatus
  workflowInstanceId: string | null
  appliedAt: string | null
  decidedAt: string | null
  decisionComment: string | null
  generatedLetterId: string | null
}

/** One field of the form paired with what was entered. */
export interface AnsweredField {
  code: string
  label: string
  fieldType: string
  value: unknown
  required: boolean
  /** In the payload but no longer part of the form. Shown, not hidden. */
  withdrawn: boolean
}

export interface RequestAttachmentView {
  fileId: string
  filename: string
  contentType: string | null
  sizeBytes: number
}

export interface GenericRequestDecisionContext {
  request: GenericRequestView
  employeeName: string | null
  requestTypeName: string | null
  requestTypeCategory: string | null
  slaHours: number | null
  answers: AnsweredField[]
  attachments: RequestAttachmentView[]
  previousOfSameType: number
  producesLetter: boolean
}

export interface LetterTemplateView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  subject: string | null
  bodyHtml: string
  pageSize: string
  marginMm: number
  active: boolean
  seeded: boolean
}

export interface GeneratedLetterView {
  id: string
  letterTemplateId: string
  templateName: string | null
  subjectType: string
  subjectId: string
  employeeId: string | null
  storedFileId: string
  referenceNo: string | null
  renderedAt: string
  renderedBy: string | null
  revokedAt: string | null
  revokeReason: string | null
}

export const REQUEST_STATUS_TONE = {
  DRAFT: 'neutral',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  RETURNED: 'warning',
  CANCELLED: 'neutral',
} as const

/** Renders a payload value for display without pretending to know its type. */
export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number' || typeof value === 'string') return String(value)
  return JSON.stringify(value)
}
